/* eslint-disable arrow-body-style */

import { PageLayout } from "@src/common/PageLayout";
import { ProviderPhoto } from "@src/common/ProviderPhoto";
import { TestRecordBanner } from "@src/common/TestRecordBanner";
import { APP_ROUTES, LAYOUT_CONSTANTS } from "@src/constants";
import { BOTTOM_NAV_MOBILE_CSS } from "@src/constants/constant-css";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import type { Provider, ProviderDepartment } from "@src/interfaces/provider.interface";
import type { ProviderPhoto as ProviderPhotoData } from "@src/interfaces/provider-photo.interface";
import ClientLogsService from "@src/services/client-logs.service";
import { getCachedProviderPhoto, getProviderPhotos } from "@src/services/provider-photos.service";
import { getProviderPhotoSrc } from "@src/utils/provider-photo-utils";
import { isTestRecordBlocked } from "@src/utils/test-record-utils";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Accordion } from "@uhg-abyss/web/ui/Accordion";
import { Badge } from "@uhg-abyss/web/ui/Badge";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Router } from "@uhg-abyss/web/ui/Router";
import { Text } from "@uhg-abyss/web/ui/Text";
import { type CSSProperties, type FC, Fragment, useEffect, useRef, useState } from "react";
import { ProviderBio } from "./ProviderBio";
import { ScheduleCard } from "./ScheduleCard";
import { SectionList } from "./SectionList";

const ErrorStateContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "50vh",
});

const ProviderInfo = styled("div", {
  width: "100%",
  maxWidth: LAYOUT_CONSTANTS.maxPageWidth,
  boxSizing: "border-box",
  minHeight: "268px",
  backgroundColor: "$app.color.top.background",
  lineHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  gap: "1rem",
  borderBottom: "1px solid $app.color.default.border",
  ".provider-text": {
    padding: "20px 0",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  ".provider-name": {
    fontWeight: 700,
  },
  variants: {
    isMobile: {
      true: {
        padding: "20px 10px",
        ".provider-text": {
          marginLeft: "0px",
          textAlign: "center",
        },
        ".provider-name": {
          fontSize: "14px",
          lineHeight: "18px",
        },
      },
      false: {
        padding: "0 40px",
        ".provider-text": {
          marginLeft: "32px",
          textAlign: "left",
        },
        ".provider-name": {
          fontSize: "36.48px",
          lineHeight: "48px",
        },
      },
    },
  },
});

const StyledBackgroundDiv = styled("div", {
  width: "100%",
  top: 0,
  left: 0,
  zIndex: 2,
  position: "relative",
});

const StyledProviderContainer = styled("div", {
  display: "flex",
  maxWidth: "1160px",
  width: "100%",
  minWidth: 0,
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
      },
      false: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: "0",
      },
    },
  },
});

const ProviderImageContainer = styled("div", {
  border: "1px solid $app.color.border.muted",
  backgroundColor: "$app.color.text.primary",
  borderRadius: "8px",
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  margin: "20px 0",
  display: "flex",
  overflow: "hidden",
  variants: {
    isMobile: {
      true: {
        margin: "unset",
        padding: "18px",
        maxWidth: "100px",
        maxHeight: "100px",
      },
      false: {
        padding: "36px",
        maxWidth: "200px",
        maxHeight: "200px",
      },
    },
    hasProviderPhoto: {
      true: {
        padding: "0",
        backgroundColor: "transparent",
      },
    },
  },
});

const BadgesContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  variants: {
    isMobile: {
      true: {
        marginTop: "4px",
        justifyContent: "center",
      },
      false: {
        marginTop: "0",
        justifyContent: "flex-start",
      },
    },
  },
});

const BottomNavContainer = styled("div", {
  ...BOTTOM_NAV_MOBILE_CSS,
  position: "fixed",
  boxSizing: "border-box",
});

const ButtonsContainer = styled("div", {
  textAlign: "center",
  width: "100%",
});

const ScheduleCardWrapper = styled("div", {
  flex: "1 1 400px",
  minWidth: "min(100%, 400px)",
});

const ProviderDetailsSection = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const BackButtonRow = styled("div", {
  width: "100%",
  padding: "16px 40px",
  display: "flex",
  justifyContent: "center",
});

const BackButtonContainer = styled("div", {
  width: "100%",
  maxWidth: "1160px",
});

const ContentWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  boxSizing: "border-box",
  variants: {
    isMobile: {
      true: { padding: "30px 20px" },
      false: { padding: "40px 40px 0" },
    },
  },
});

const ContentRow = styled("div", {
  display: "flex",
  width: "100%",
  maxWidth: "1160px",
  minWidth: 0,
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "20px",
  variants: {
    isMobile: {
      true: { flexDirection: "column" },
      false: { flexDirection: "row" },
    },
  },
});

const LeftColumn = styled("div", {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 520px",
  minWidth: 0,
  width: "100%",
  gap: "32px",
  marginBottom: "40px",
  variants: {
    isMobile: {
      true: { textAlign: "center" },
      false: { textAlign: "left" },
    },
  },
});

const AgeList = styled("ul", {
  margin: "8px 0 0",
  paddingLeft: "10px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const AgeListItem = styled("li", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const LocationsList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  textAlign: "left",
});

const LocationDivider = styled("hr", {
  border: "none",
  borderTop: "1px solid $app.color.border.muted",
  margin: "10px 0",
});

type LocationsProps = {
  locations: ProviderDepartment[];
  textCss: CSSProperties;
};

type ProviderScheduleSectionProps = {
  dataUserUuid?: string;
  isMobile: boolean;
  isTablet: boolean;
  isSmallestMobile: boolean;
  isScheduleModalOpen: boolean;
  onBack: () => void;
  onClose: () => void;
  onOpen: () => void;
  providerDetails: Provider;
};

const getLocationKey = (location: ProviderDepartment) => {
  return `${location.department_id ?? "unknown"}-${location.clinic_name ?? "clinic"}-${location.address_line_1 ?? "address"}`;
};
const Locations: FC<LocationsProps> = ({ locations, textCss }) => {
  if (locations.length === 1) {
    const location = locations[0];
    return (
      <Fragment>
        <Text css={{ "abyss-text-root": textCss }}>{location.clinic_name ?? ""}</Text>
        <Text css={{ "abyss-text-root": textCss }}>
          {location.address_line_1 ?? ""}
          {location.address_line_2 ? `, ${location.address_line_2}` : ""}
        </Text>
        <Text css={{ "abyss-text-root": textCss }}>
          {[location.city, location.state].filter(Boolean).join(", ")}
        </Text>
      </Fragment>
    );
  }

  if (locations.length === 0) {
    return <Text css={{ "abyss-text-root": textCss }}>Clinic location unavailable</Text>;
  }

  const uniqueStates = Array.from(new Set(locations.map((location) => location.state)));
  const states = uniqueStates.join(", ");
  return (
    <Fragment>
      <Text css={{ "abyss-text-root": textCss }}>Multiple locations</Text>
      <Text css={{ "abyss-text-root": textCss }}>{states}</Text>
    </Fragment>
  );
};

const ProviderScheduleSection: FC<ProviderScheduleSectionProps> = ({
  dataUserUuid,
  isMobile,
  isTablet,
  isSmallestMobile,
  isScheduleModalOpen,
  onBack,
  onClose,
  onOpen,
  providerDetails,
}) => {
  const scheduleCard = (
    <ScheduleCard
      isMobile={isMobile}
      isTablet={isTablet}
      isSmallestMobile={isSmallestMobile}
      providerDetails={providerDetails}
    />
  );

  if (!isMobile) {
    return <ScheduleCardWrapper>{scheduleCard}</ScheduleCardWrapper>;
  }

  return (
    <Fragment>
      <BottomNavContainer>
        <ButtonsContainer>
          <Button
            data-testid="test-id-provider-profile-mobile-schedule-button"
            size="lg"
            css={{ width: "100%" }}
            id="obc-provider-profile-schedule-appointment-btn-mobile"
            data-obc-label="Schedule first appointment mobile button"
            onClick={onOpen}
          >
            Schedule first appointment
          </Button>
          <Button
            data-testid="test-id-provider-profile-mobile-back-button"
            id="obc-provider-profile-back-btn-mobile"
            data-obc-label="Provider Profile Back mobile button"
            onClick={onBack}
            variant="text"
            size="lg"
          >
            Back
          </Button>
        </ButtonsContainer>
      </BottomNavContainer>
      <ModalDialog
        size="fullscreen"
        css={{
          "abyss-modal-base-content-container": {
            height: "100%",
            width: "100%",
          },
          "abyss-modal-base-body": {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: "hidden",
            padding: isSmallestMobile ? "16px" : "24px",
            height: "100%",
          },
        }}
        data-id="obc-provider-profile-schedule-modal-dialog"
        data-obc-label="Provider Profile Schedule Modal Dialog"
        isOpen={isScheduleModalOpen}
        onClose={() => {
          ClientLogsService.logClick(
            dataUserUuid || "unknown",
            "ProviderProfile.renderScheduleCard",
            "obc-provider-profile-schedule-modal-close-btn",
            "Provider Profile Schedule Modal Close Button",
          );
          onClose();
        }}
      >
        {scheduleCard}
      </ModalDialog>
    </Fragment>
  );
};
export const ProviderProfile = () => {
  const { data, setData } = useApplicationContext();
  const providerDetails =
    typeof data.selectedProvider === "object" && data.selectedProvider !== null
      ? data.selectedProvider
      : ({} as Provider);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [providerPhoto, setProviderPhoto] = useState<ProviderPhotoData | undefined>(
    providerDetails.providerPhoto,
  );
  const [providerPhotoIsLoading, setProviderPhotoIsLoading] = useState(false);
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const isSmallestMobile = useMediaQuery(MEDIA_QUERIES.smallestMobileMax);
  const navigate = useNavigateWithUTM();
  const hasProviderPhoto = Boolean(getProviderPhotoSrc(providerPhoto));
  const providerSelectionSourcePathRef = useRef(
    data.providerSelectionSource === APP_ROUTES.MEET_THE_TEAM
      ? APP_ROUTES.GET_STARTED
      : APP_ROUTES.FIND_PROVIDER,
  );
  const backToProviderSelection = () => {
    setData("selectedDate", null);
    setData("selectedDepartment", null);
    return navigate(providerSelectionSourcePathRef.current);
  };

  // Record that the user entered patient-information from /provider-profile so the
  // Back button returns here regardless of which code path triggers navigation.
  useEffect(() => {
    setData("providerBackPath", APP_ROUTES.PROVIDER_PROFILE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setProviderPhoto(providerDetails.providerPhoto);
    setProviderPhotoIsLoading(false);

    if (!providerDetails.providerId || !Number.isInteger(providerDetails.contextId)) {
      return;
    }

    const providerPhotoKey = {
      contextId: providerDetails.contextId,
      providerId: String(providerDetails.providerId),
    };
    const cachedProviderPhoto = getCachedProviderPhoto(providerPhotoKey);
    if (cachedProviderPhoto) {
      setProviderPhoto(cachedProviderPhoto);
      return;
    }

    let shouldUpdate = true;
    setProviderPhotoIsLoading(true);
    getProviderPhotos({
      providers: [providerPhotoKey],
      user_uuid: data.userUuid,
    })
      .then((response) => {
        if (!shouldUpdate) {
          return;
        }

        if (!response.success) {
          return;
        }

        const nextProviderPhoto = response.data?.photos?.[0];
        setProviderPhoto(nextProviderPhoto);
      })
      .catch(() => undefined)
      .finally(() => {
        if (shouldUpdate) {
          setProviderPhotoIsLoading(false);
        }
      });

    return () => {
      shouldUpdate = false;
    };
  }, [
    providerDetails.providerId,
    providerDetails.contextId,
    providerDetails.providerPhoto,
    data.userUuid,
  ]);

  if (providerDetails === null || Object.keys(providerDetails).length === 0) {
    return (
      <PageLayout backgroundColor={"$app.color.default.background"}>
        <ErrorStateContainer>
          <Text css={{ fontSize: "18px", color: "red", textAlign: "center" }}>
            Unable to load provider information. Please try again later.
          </Text>
        </ErrorStateContainer>
      </PageLayout>
    );
  }

  // Sort locations alphabetically by clinicName
  const sortedLocations = [...(providerDetails.departments || [])].sort((a, b) => {
    const nameA = a.clinic_name?.toLowerCase() || "";
    const nameB = b.clinic_name?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  });
  const accordionRootCss = {
    "abyss-accordion-root": { borderTop: "none" },
  };

  const accordionHeaderCss = {
    "abyss-icon-symbol": { color: "$app.color.brand.primary !important" },
    "abyss-accordion-header-trigger": {
      backgroundColor: "$app.color.default.background !important",
      padding: "16px 0 !important",
    },
  };

  const accordionContentCss = {
    "abyss-accordion-content-container": {
      backgroundColor: "$app.color.default.background !important",
      border: "none",
    },
    "abyss-accordion-content-wrapper": {
      padding: "0 !important",
      marginTop: "8px",
    },
  };

  return (
    <Fragment>
      <Router.MetaTags title="Provider Profile" />
      <PageLayout
        backgroundColor={"$app.color.default.background"}
        css={{
          padding: "0px", // Override the default padding
        }}
      >
        <StyledBackgroundDiv>
          <ProviderInfo isMobile={isMobile}>
            <StyledProviderContainer isMobile={isMobile}>
              <ProviderImageContainer isMobile={isMobile} hasProviderPhoto={hasProviderPhoto}>
                <ProviderPhoto
                  providerPhoto={providerPhoto}
                  altText={`${providerDetails?.fullName ?? "Provider"} photo`}
                  fallbackIconSize={128}
                  fallbackIconStyle={{ width: "128px", height: "128px" }}
                  isLoading={providerPhotoIsLoading}
                  loadingLabel={`Loading ${providerDetails?.fullName ?? "provider"} photo`}
                />
              </ProviderImageContainer>
              <div className="provider-text">
                <Heading className="provider-name">
                  {[providerDetails?.fullName, providerDetails?.providerType]
                    .filter((value) => Boolean(value && value !== ""))
                    .join(", ")}
                </Heading>
                <Text css={{ fontSize: isMobile ? "14px" : "18px" }}>
                  {providerDetails.clinicianType}
                </Text>
                <Locations
                  locations={sortedLocations}
                  textCss={{ fontSize: isMobile ? "14px" : "18px" }}
                />
                <BadgesContainer isMobile={isMobile}>
                  {providerDetails.modality?.includes("Virtual") && (
                    <Badge
                      outline
                      variant="warning"
                      icon={{ icon: "computer", variant: "outlined" }}
                    >
                      Virtual
                    </Badge>
                  )}
                  {providerDetails.modality?.some((type) => type.toLowerCase() === "in-person") && (
                    <Badge outline variant="warning" icon={{ icon: "person", variant: "outlined" }}>
                      In person
                    </Badge>
                  )}
                </BadgesContainer>
              </div>
            </StyledProviderContainer>
          </ProviderInfo>

          <TestRecordBanner visible={isTestRecordBlocked(data)} />
          <ProviderDetailsSection>
            {/* Start - Provider specific details, locations and scheduling widget*/}
            <ContentWrapper isMobile={isMobile}>
              <ContentRow isMobile={isMobile}>
                <LeftColumn isMobile={isMobile}>
                  {/* Section 1: About me and my practice - expanded by default */}
                  {providerDetails.providerBio && providerDetails.providerBio !== "" && (
                    <Accordion isCollapsible defaultValue="about-me" css={accordionRootCss}>
                      <Accordion.Item value="about-me">
                        <Accordion.Header css={accordionHeaderCss}>
                          <Heading
                            size="sm"
                            css={{
                              "abyss-heading-root": {
                                lineHeight: isMobile ? "24px" : "28px",
                              },
                            }}
                          >
                            About me and my practice
                          </Heading>
                        </Accordion.Header>
                        <Accordion.Content css={accordionContentCss}>
                          <ProviderBio
                            providerBio={providerDetails.providerBio}
                            showHeading={false}
                          />
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion>
                  )}

                  {/* Section 2: Age range served - collapsed by default */}
                  {providerDetails.agePreferences && providerDetails.agePreferences.length > 0 && (
                    <Accordion isCollapsible css={accordionRootCss}>
                      <Accordion.Item value="age-range-served">
                        <Accordion.Header css={accordionHeaderCss}>
                          <Heading
                            size="sm"
                            css={{
                              "abyss-heading-root": {
                                lineHeight: isMobile ? "24px" : "28px",
                              },
                            }}
                          >
                            Age range served
                          </Heading>
                        </Accordion.Header>
                        <Accordion.Content css={accordionContentCss}>
                          <AgeList>
                            {providerDetails.agePreferences.map((range) => (
                              <AgeListItem key={range}>
                                <span>•</span>
                                <Text css={{ fontSize: isMobile ? "14px" : "16px" }}>
                                  {range.toLowerCase().includes("geriatric")
                                    ? "Older Adult (65+)"
                                    : range}
                                </Text>
                              </AgeListItem>
                            ))}
                          </AgeList>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion>
                  )}

                  {/* Section 3: I specialize in - expanded by default */}
                  {providerDetails.conditionPreferences &&
                    providerDetails.conditionPreferences.length > 0 && (
                      <Accordion
                        isCollapsible
                        defaultValue="i-specialize-in"
                        css={accordionRootCss}
                      >
                        <Accordion.Item value="i-specialize-in">
                          <Accordion.Header css={accordionHeaderCss}>
                            <Heading
                              size="sm"
                              css={{
                                "abyss-heading-root": {
                                  lineHeight: isMobile ? "24px" : "28px",
                                },
                              }}
                            >
                              I specialize in
                            </Heading>
                          </Accordion.Header>
                          <Accordion.Content css={accordionContentCss}>
                            <SectionList
                              title="I specialize in"
                              items={providerDetails.conditionPreferences}
                              showHeading={false}
                            />
                          </Accordion.Content>
                        </Accordion.Item>
                      </Accordion>
                    )}

                  {/* Section 4: Locations - expanded by default */}
                  {sortedLocations.length > 0 && (
                    <Accordion isCollapsible defaultValue="locations" css={accordionRootCss}>
                      <Accordion.Item value="locations">
                        <Accordion.Header css={accordionHeaderCss}>
                          <Heading
                            level={2}
                            size="sm"
                            css={{
                              "abyss-heading-root": {
                                lineHeight: isMobile ? "24px" : "28px",
                              },
                            }}
                          >
                            Locations
                          </Heading>
                        </Accordion.Header>
                        <Accordion.Content css={accordionContentCss}>
                          <LocationsList>
                            {sortedLocations.map((location, index) => (
                              <Fragment key={getLocationKey(location)}>
                                <Locations locations={[location]} textCss={{ fontSize: "18px" }} />
                                {index < sortedLocations.length - 1 && <LocationDivider />}
                              </Fragment>
                            ))}
                          </LocationsList>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </LeftColumn>
                <ProviderScheduleSection
                  dataUserUuid={data.userUuid}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  isSmallestMobile={isSmallestMobile}
                  isScheduleModalOpen={isScheduleModalOpen}
                  onBack={backToProviderSelection}
                  onClose={() => {
                    setIsScheduleModalOpen(false);
                  }}
                  onOpen={() => {
                    setIsScheduleModalOpen(true);
                  }}
                  providerDetails={providerDetails}
                />
              </ContentRow>
            </ContentWrapper>
            {/* End - Provider specific details, locations and scheduling widget*/}
            {!isMobile && (
              <BackButtonRow>
                <BackButtonContainer data-testid="test-id-provider-profile-back-button">
                  <Button
                    icon={{ icon: "chevron_backward", position: "leading" }}
                    size="md"
                    variant="outline"
                    id="obc-provider-profile-back-btn-desktop"
                    data-testid="test-id-provider-profile-desktop-back-button"
                    data-obc-label="Provider Profile Back Button Desktop"
                    onClick={backToProviderSelection}
                  >
                    Back
                  </Button>
                </BackButtonContainer>
              </BackButtonRow>
            )}
          </ProviderDetailsSection>
        </StyledBackgroundDiv>
      </PageLayout>
    </Fragment>
  );
};
